// 데이터 관련 유틸리티 함수들
import { BudgetData } from '../contexts/AppContext';
import { SafeMonthlyData } from '../types/payroll';

// 기본값을 가진 월별 데이터 생성 함수
export const createDefaultMonthlyData = (): SafeMonthlyData => ({
  targetSales: 0,
  actualSales: 0,
  personnelPercentage: 19,
  teamPersonnelPercentages: {},
  teamTargetSales: {},
  teamPureTargetSales: {},
  teamActualSales: {},
  teamSalaryTotals: {},
  teams: [],
  employees: [],
  doctorTeamMapping: {}, // 🎯 진료의사-팀 매핑 기본값 추가
  adjustmentApplied: {},
  teamAdjustmentCarryover: {} // 새 달은 이월 없음이 명시된 상태로 시작한다 (Codex 재심 P1-5)
});

// 안전한 월별 데이터 가져오기 함수
export const getSafeMonthlyData = (budgetData: BudgetData, month: string): SafeMonthlyData => {
  const data = budgetData.monthlyData?.[month];
  if (!data) return createDefaultMonthlyData();
  
  return {
    ...createDefaultMonthlyData(),
    ...data
  };
};

// 데이터 저장 전 검증
export const validateDataBeforeSave = (data: any, month: string): { valid: boolean; errors: string[] } => {
  const errors: string[] = [];
  
  if (!data) {
    errors.push('저장할 데이터가 없습니다.');
    return { valid: false, errors };
  }
  
  if (!month || month.trim() === '') {
    errors.push('유효하지 않은 월 정보입니다.');
  }
  
  if (data.monthlyData && data.monthlyData[month]) {
    const monthData = data.monthlyData[month];
    
    if (monthData.targetSales < 0) {
      errors.push('목표매출은 0 이상이어야 합니다.');
    }
    
    if (monthData.actualSales < 0) {
      errors.push('실제매출은 0 이상이어야 합니다.');
    }
    
    if (monthData.employees && !Array.isArray(monthData.employees)) {
      errors.push('직원 데이터 형식이 올바르지 않습니다.');
    }
  }
  
  return { valid: errors.length === 0, errors };
};

// 월별 데이터 비교
export const compareMonthlyData = (
  currentData: SafeMonthlyData, 
  comparisonData: SafeMonthlyData
): {
  targetSalesDiff: number;
  actualSalesDiff: number;
  employeeCountDiff: number;
  teamCountDiff: number;
} => {
  return {
    targetSalesDiff: currentData.targetSales - comparisonData.targetSales,
    actualSalesDiff: currentData.actualSales - comparisonData.actualSales,
    employeeCountDiff: (currentData.employees?.length || 0) - (comparisonData.employees?.length || 0),
    teamCountDiff: currentData.teams.length - comparisonData.teams.length
  };
};

// 월별 데이터 복사 (새 월로)
export const copyMonthlyData = (
  sourceData: SafeMonthlyData, 
  targetMonth: string,
  options: {
    copyEmployees: boolean;
    copyTeams: boolean;
    copyTargetSales: boolean;
    resetActualSales: boolean;
  }
): SafeMonthlyData => {
  const copiedData: SafeMonthlyData = {
    ...createDefaultMonthlyData(),
    targetSales: options.copyTargetSales ? sourceData.targetSales : 0,
    actualSales: options.resetActualSales ? 0 : sourceData.actualSales,
    personnelPercentage: sourceData.personnelPercentage,
    lastUpdated: new Date().toISOString(),
    importedFromMonth: targetMonth,
    dataSource: '월별복사'
  };
  
  if (options.copyEmployees && sourceData.employees) {
    copiedData.employees = sourceData.employees.map(emp => ({ ...emp }));
  }
  
  if (options.copyTeams) {
    copiedData.teams = [...sourceData.teams];
    copiedData.teamPersonnelPercentages = { ...sourceData.teamPersonnelPercentages };
    
    if (options.copyTargetSales) {
      copiedData.teamTargetSales = { ...sourceData.teamTargetSales };
      copiedData.teamPureTargetSales = { ...sourceData.teamPureTargetSales };
    }
  }
  
  return copiedData;
};

// 숫자 형식화 (천 단위 쉼표)
export const formatNumber = (num: number): string => {
  return num.toLocaleString();
};

// 비율 형식화 (소수점 1자리)
export const formatPercentage = (ratio: number): string => {
  return `${(ratio * 100).toFixed(1)}%`;
};

// 통화 형식화
export const formatCurrency = (amount: number): string => {
  return `${formatNumber(amount)}원`;
};

// 날짜 형식화
export const formatDate = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return dateString;
  }
};